import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APP_STORE_URL, PLAY_STORE_URL } from './constants';
import { inferAppStorePlatform } from './inferAppStorePlatform';
import StorePickerPopover, {
  type StorePickerPopoverAlign,
  type StorePickerPopoverPlacement,
} from './StorePickerPopover';

type StoreDownloadLinkProps = {
  className?: string;
  children: React.ReactNode;
  tabIndex?: number;
  'aria-label'?: string;
  'data-node-id'?: string;
  popoverAlign?: StorePickerPopoverAlign;
  popoverPlacement?: StorePickerPopoverPlacement;
};

/**
 * Direct App Store / Play Store link when UA suggests a platform; otherwise opens anchored store picker.
 */
const StoreDownloadLink: React.FC<StoreDownloadLinkProps> = ({
  className,
  children,
  tabIndex,
  'aria-label': ariaLabel,
  'data-node-id': dataNodeId,
  popoverAlign = 'end',
  popoverPlacement = 'below',
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const platform = useMemo(() => inferAppStorePlatform(), []);

  const href = platform === 'ios' ? APP_STORE_URL : platform === 'android' ? PLAY_STORE_URL : null;

  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePicker();
    };
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closePicker();
      }
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [pickerOpen, closePicker]);

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        data-node-id={dataNodeId}
      >
        {children}
      </a>
    );
  }

  /** Center popover under control: full-width wrap only when the button is full-width (below `md` in hero). From `md` up, hug the button so the flex row does not get a stretched hit box on desktop. */
  const wrapClass =
    popoverAlign === 'center'
      ? 'relative block w-full [isolation:isolate] md:inline-flex md:w-auto'
      : 'relative inline-flex [isolation:isolate]';

  return (
    <span ref={containerRef} className={wrapClass}>
      <button
        type="button"
        className={className}
        tabIndex={tabIndex}
        aria-label={ariaLabel}
        aria-expanded={pickerOpen}
        aria-haspopup="menu"
        data-node-id={dataNodeId}
        onClick={openPicker}
      >
        {children}
      </button>
      {pickerOpen ? (
        <StorePickerPopover align={popoverAlign} placement={popoverPlacement} onClose={closePicker} />
      ) : null}
    </span>
  );
};

export default StoreDownloadLink;
